/* eslint-disable class-methods-use-this */
import ReduceStore from "flux/lib/FluxReduceStore";

import CommentStatusFilter, { AIReviewCommentStatusFilter } from "../../../foundation/model/RemarkStatusFilters";
import RemarkType from "../../../foundation/model/RemarkType";
import BasicRecord from "../../../foundation/utils/BasicRecord";
import { isValentines } from "../../../foundation/utils/celebration";
import { isDraftDot, isDraftAnnotation } from "../../../foundation/utils/dot";
import { PartializeProps } from "../../../foundation/utils/UtilityTypes";
import ColorName from "../../../library/ColorDot/ColorName";
import * as ApprovalsActionPayloads from "../approvals/ApprovalsActionPayloads";
import ApprovalsActionTypes from "../approvals/ApprovalsActionTypes";
import * as BarrelActionPayloads from "../barrel/BarrelActionPayloads";
import BarrelActionTypes from "../barrel/BarrelActionTypes";
import ComponentActionTypes from "../component/ComponentActionTypes";
import InspectableViewActionTypes from "../inspectableView/InspectableViewActionTypes";

import ProjectActionTypes from "../project/ProjectActionTypes";
import { AllPayloads } from "../payloads";

import * as ScreenActionPayloads from "./ScreenActionPayloads";
import ScreenActionTypes from "./ScreenActionTypes";

type State = {
    loadingDots: boolean;
    loadingProjectDots: boolean;
    aiStatusFilter: AIReviewCommentStatusFilter;
    statusFilter: CommentStatusFilter;
    colorFilter: ColorName | null;
    aiReviewTypeFilter: BasicRecord<boolean>;
    lastNoteColorUsed: string;
    flash: boolean;
    dotId: string | null;
    hoveredDotId: string | null;
    highlightedDots: string[];
    highlightedComments: string[];
    savedNotes: {
        [sid: string]: {
            [dotId: string]: string;
        };
    };
    valentinesDates: Record<number, { sid: string; status: "unread" | "seen" | "dismissed"; } | undefined>;
};

class DotsStore extends ReduceStore<State, AllPayloads> {
    getInitialState(): State {
        return {
            loadingDots: true,
            loadingProjectDots: true,
            statusFilter: CommentStatusFilter.Open,
            aiStatusFilter: AIReviewCommentStatusFilter.All,
            colorFilter: null,
            lastNoteColorUsed: "yellow",
            flash: false,
            dotId: null,
            hoveredDotId: null,
            highlightedDots: [],
            highlightedComments: [],
            aiReviewTypeFilter: {},
            savedNotes: {},
            valentinesDates: {}
        };
    }

    reset(state: State): State {
        const { savedNotes, valentinesDates } = state;

        return {
            ...this.getInitialState(),
            savedNotes,
            valentinesDates
        };
    }

    load(state: State, {
        dots: update = {}
    }: PartializeProps<BarrelActionPayloads.Load, "dots">): State {
        const { savedNotes } = state;

        return {
            ...state,
            ...update,
            savedNotes
        };
    }

    setDots(state: State, {
        dotId,
        preloadScreenData,
        statusFilter,
        colorFilter
    }: ScreenActionPayloads.SetDots): State {
        // future navigations should not overwrite current screen dots' view states
        if (preloadScreenData) {
            return state;
        }

        return {
            ...state,
            loadingDots: false,
            dotId,
            statusFilter,
            colorFilter
        };
    }

    setDotsForIntermediateLoading(state: State, {
        dotsDetails: {
            dotId
        },
        openScreenData: {
            commentId
        },
        barrelData: {
            dots: update
        }
    }: BarrelActionPayloads.LoadIntermediate): State {
        if (state.dotId === dotId) {
            return {
                ...state,
                ...update,
                loadingDots: false,
                dotId
            };
        }

        let highlightedComments: string[] = [];
        if (Array.isArray(commentId)) {
            highlightedComments = commentId;
        } else if (commentId) {
            highlightedComments = [commentId];
        }

        return {
            ...state,
            ...update,
            loadingDots: false,
            dotId,
            highlightedComments,
            highlightedDots: state.highlightedDots.filter(id => id !== dotId)
        };
    }

    createNote(state: State, {
        dot: {
            _id: dotId
        }
    }: ScreenActionPayloads.CreateNote): State {
        return {
            ...state,
            dotId,
            statusFilter: CommentStatusFilter.Open,
            colorFilter: null
        };
    }

    updateDot(state: State, {
        did: oldDid,
        dotData: {
            _id: newDid
        }
    }: ScreenActionPayloads.UpdateDot): State {
        if (state.dotId !== oldDid && oldDid === newDid) {
            return state;
        }

        return {
            ...state,
            dotId: newDid
        };
    }

    updateDotStatus(state: State, {
        did: dotId,
        status,
        statusFilter: newStatusFilter
    }: ScreenActionPayloads.UpdateDotStatus): State {
        const statusFilter = newStatusFilter || state.statusFilter;

        if (state.dotId === dotId && statusFilter as string !== status) {
            // If status of currently opened dot is being changed
            // and status filter (current or next) doesn't match with current status filter
            // unselect the dot
            return {
                ...state,
                statusFilter,
                dotId: null
            };
        }

        if (newStatusFilter) {
            return {
                ...state,
                statusFilter
            };
        }

        return state;
    }

    updateAIDotStatus(state: State, {
        did: dotId,
        status,
        statusFilter: newStatusFilter
    }: ApprovalsActionPayloads.UpdateAIReviewScreenDotStatus): State {
        const statusFilter = newStatusFilter ?? state.statusFilter;

        if (state.dotId === dotId && statusFilter as string !== status) {
            // If status of currently opened dot is being changed
            // and status filter (current or next) doesn't match with current status filter
            // unselect the dot
            return {
                ...state,
                statusFilter,
                dotId: null
            };
        }

        if (newStatusFilter) {
            return {
                ...state,
                statusFilter
            };
        }

        return state;
    }

    updateForUpdateAIReview(state: State): State {
        return {
            ...state,
            aiStatusFilter: AIReviewCommentStatusFilter.All,
            aiReviewTypeFilter: {},
            dotId: null
        };
    }

    updateLastNoteColorUsed(state: State, {
        colorName: lastNoteColorUsed
    }: ScreenActionPayloads.UpdateDotColor): State {
        return {
            ...state,
            lastNoteColorUsed
        };
    }

    // eslint-disable-next-line max-params
    saveNote(
        state: State,
        sid: string,
        did: string,
        approvalsNote: boolean,
        note: string
    ): State {
        const { savedNotes } = state;
        // eslint-disable-next-line no-nested-ternary
        const dotId = isDraftDot(did)
            ? approvalsNote
                ? "approvalsNewDot"
                : "newDot"
            : did;

        return {
            ...state,
            savedNotes: {
                ...savedNotes,
                [sid]: {
                    ...savedNotes[sid],
                    [dotId]: note
                }
            }
        };
    }

    removeDot(state: State, {
        sid,
        did,
        approvalsNote,
        note
    }: ScreenActionPayloads.RemoveDot): State {
        let { savedNotes } = state;
        if (isDraftDot(did)) {
            savedNotes = {
                ...savedNotes,
                [sid]: {
                    ...savedNotes[sid],
                    [approvalsNote ? "approvalsNewDot" : "newDot"]: note!
                }
            };
        }

        if (state.dotId !== did) {
            if (!isDraftDot(did)) {
                return state;
            }

            return {
                ...state,
                savedNotes
            };
        }

        return {
            ...state,
            savedNotes,
            dotId: null
        };
    }

    removeAIReviewDot(state: State, {
        did
    }: ApprovalsActionPayloads.DeleteAIReviewScreenDot): State {
        if (state.dotId !== did) {
            return state;
        }

        return {
            ...state,
            dotId: null
        };
    }

    changeDotFilter(state: State, {
        status: statusFilter,
        color: colorFilter,
        dotId,
        aiReviewMode
    }: ScreenActionPayloads.ChangeDotFilter): State {
        return {
            ...state,
            colorFilter,
            [aiReviewMode ? "aiStatusFilter" : "statusFilter"]: statusFilter,
            dotId
        };
    }

    changeAIDotFilter(state: State, {
        typeFilter
    }: ScreenActionPayloads.ChangeAIDotFilter): State {
        return {
            ...state,
            aiReviewTypeFilter: typeFilter
        };
    }

    flash(state: State, {
        flash
    }: ScreenActionPayloads.EnableNoteMode): State {
        const dotId = flash ? null : state.dotId; // deselect dot to close popup

        return {
            ...state,
            flash,
            dotId
        };
    }

    dotsFlashed(state: State): State {
        return {
            ...state,
            flash: false
        };
    }

    openScreen(state: State, {
        dotId,
        commentId
    }: ScreenActionPayloads.OpenScreen): State {
        if (state.dotId === dotId) {
            return state;
        }

        let highlightedComments: string[] = [];
        if (Array.isArray(commentId)) {
            highlightedComments = commentId;
        } else if (commentId) {
            highlightedComments = [commentId];
        }

        return {
            ...state,
            dotId,
            highlightedComments,
            highlightedDots: state.highlightedDots.filter(id => id !== dotId)
        };
    }

    selectDot(state: State, {
        did,
        commentId,
        statusFilter,
        colorFilter
    }: ScreenActionPayloads.SelectDot): State {
        if (state.dotId === did) {
            return state;
        }

        let highlightedComments: string[] = [];
        if (Array.isArray(commentId)) {
            highlightedComments = commentId;
        } else if (commentId) {
            highlightedComments = [commentId];
        }

        const updatedState = {
            ...state,
            dotId: did,
            highlightedComments,
            highlightedDots: state.highlightedDots.filter(id => id !== did)
        };

        if (statusFilter !== undefined) {
            updatedState.statusFilter = statusFilter;
        }

        if (colorFilter !== undefined) {
            updatedState.colorFilter = colorFilter;
        }

        return updatedState;
    }

    setHoveredDot(state: State, {
        did
    }: ScreenActionPayloads.SetHoveredDot) {
        if (state.hoveredDotId === did) {
            return state;
        }

        return {
            ...state,
            hoveredDotId: did
        };
    }

    deselectDot(state: State): State {
        return {
            ...state,
            dotId: null
        };
    }

    unhighlightComment(state: State, {
        cmid
    }: ScreenActionPayloads.UnhighlightComment): State {
        return {
            ...state,
            highlightedComments: state.highlightedComments.filter(id => id !== cmid)
        };
    }

    updateDotPopupRemarkType(state: State, {
        remark,
        did,
        remarkType,
        sid,
        textContent
    }: ScreenActionPayloads.UpdateDotPopupRemarkType): State {
        if (remarkType === RemarkType.Dot) {
            const { savedNotes } = state;
            return {
                ...state,
                dotId: remark!._id,
                savedNotes: {
                    ...savedNotes,
                    [sid]: {
                        ...savedNotes[sid],
                        [remark!._id]: textContent ?? ""
                    }
                },
                statusFilter: CommentStatusFilter.Open,
                colorFilter: null
            };
        }

        if (state.dotId === did) {
            return {
                ...state,
                dotId: null
            };
        }

        return state;
    }

    handleAddAnnotation(state: State, { sid, annotation: { _id: atid } }: ScreenActionPayloads.AddAnnotation): State {
        if (!isDraftAnnotation(atid)) {
            return state;
        }

        const { savedNotes } = state;
        return {
            ...state,
            savedNotes: {
                ...savedNotes,
                [sid]: {
                    ...savedNotes[sid],
                    newDot: ""
                }
            }
        };
    }

    handleChangeSelectedView(state: State): State {
        const { savedNotes, lastNoteColorUsed, loadingProjectDots, valentinesDates } = state;

        return {
            ...this.getInitialState(),
            savedNotes,
            lastNoteColorUsed,
            loadingProjectDots,
            valentinesDates
        };
    }

    handleOpenComponent(state: State): State {
        const { valentinesDates } = state;

        return {
            ...this.getInitialState(),
            loadingDots: false,
            valentinesDates
        };
    }

    getProjectDots(state: State): State {
        return {
            ...state,
            loadingProjectDots: true
        };
    }

    setProjectDots(state: State): State {
        return {
            ...state,
            loadingProjectDots: false
        };
    }

    addValentinesDot(state: State, screenId: string): State {
        if (!isValentines()) {
            return state;
        }

        const currentYear = new Date().getFullYear();

        if (state.valentinesDates[currentYear] !== undefined) {
            return state;
        }

        return {
            ...state,
            valentinesDates: {
                ...state.valentinesDates,
                [currentYear]: {
                    sid: screenId,
                    status: "unread"
                }
            }
        };
    }

    markValentinesDotAsSeen(state: State): State {
        const currentYear = new Date().getFullYear();

        return {
            ...state,
            valentinesDates: {
                ...state.valentinesDates,
                [currentYear]: {
                    ...state.valentinesDates[currentYear]!,
                    status: "seen"
                }
            }
        };
    }

    dismissValentinesDot(state: State): State {
        const currentYear = new Date().getFullYear();

        return {
            ...state,
            valentinesDates: {
                ...state.valentinesDates,
                [currentYear]: {
                    ...state.valentinesDates[currentYear]!,
                    status: "dismissed"
                }
            }
        };
    }

    // eslint-disable-next-line complexity
    reduce(state: State, action: AllPayloads): State {
        switch (action.type) {
            // Barrel action reducers
            case BarrelActionTypes.RESET:
                return this.reset(state);
            case BarrelActionTypes.CHANGE_SELECTED_VIEW:
                return this.handleChangeSelectedView(state);
            case BarrelActionTypes.LOAD:
                return this.load(state, action);
            case BarrelActionTypes.LOAD_INTERMEDIATE: {
                const updatedState = this.setDotsForIntermediateLoading(state, action);

                return this.addValentinesDot(updatedState, action.screenData.sid);
            }

            // Screen action reducers
            case ScreenActionTypes.SET_DOTS:
                return this.setDots(state, action);

            case ScreenActionTypes.CREATE_NOTE:
                return this.createNote(state, action);

            case ScreenActionTypes.UPDATE_DOT:
                return this.updateDot(state, action);

            case ScreenActionTypes.UPDATE_DOT_STATUS:
                return this.updateDotStatus(state, action);
            case ApprovalsActionTypes.UPDATE_AI_REVIEW_SCREEN_DOT_STATUS:
                return this.updateAIDotStatus(state, action);

            case ApprovalsActionTypes.UPDATE_AI_REVIEW:
                return this.updateForUpdateAIReview(state);

            case ScreenActionTypes.UPDATE_DOT_COLOR:
                return this.updateLastNoteColorUsed(state, action);

            case ScreenActionTypes.SAVE_NOTE:
                return this.saveNote(state, action.sid, action.did, action.approvalsNote, action.note);
            case ScreenActionTypes.ADD_DOT:
            case ScreenActionTypes.ADD_APPROVAL_PENDING_DOT:
                return this.saveNote(state, action.sid, action.dot._id, false, "");
            case ScreenActionTypes.CREATE_COMMENT:
                return this.saveNote(state, action.sid, action.did, false, "");

            case ScreenActionTypes.REMOVE_DOT:
                return this.removeDot(state, action);

            case ApprovalsActionTypes.DELETE_AI_REVIEW_SCREEN_DOT:
                return this.removeAIReviewDot(state, action);

            case ScreenActionTypes.CHANGE_DOT_FILTER:
                return this.changeDotFilter(state, action);

            case ScreenActionTypes.CHANGE_AI_DOT_FILTER:
                return this.changeAIDotFilter(state, action);

            case ScreenActionTypes.ENABLE_NOTE_MODE:
                return this.flash(state, action);

            case ScreenActionTypes.DOTS_FLASHED:
                return this.dotsFlashed(state);

            case ScreenActionTypes.OPEN_SCREEN: {
                const updatedState = this.openScreen(state, action);

                return this.addValentinesDot(updatedState, action.screenId);
            }

            case ScreenActionTypes.SELECT_DOT:
                return this.selectDot(state, action);

            case ScreenActionTypes.SET_HOVERED_DOT:
                return this.setHoveredDot(state, action);

            case ScreenActionTypes.MARK_VALENTINES_DOT_AS_SEEN:
                return this.markValentinesDotAsSeen(state);

            case ScreenActionTypes.DISMISS_VALENTINES_DOT:
                return this.dismissValentinesDot(state);

            case InspectableViewActionTypes.SELECT_LAYER: {
                if (!action.shouldKeepDotOrAnnotationSelected) {
                    return this.deselectDot(state);
                }

                return state;
            }

            case InspectableViewActionTypes.DESELECT_LAYER:
            case ScreenActionTypes.CLOSE_DOT_POPUP:
            case ScreenActionTypes.OPEN_DOT_MENU:
            case ScreenActionTypes.SHOW_FLOW_LINKS:
            case ScreenActionTypes.SELECT_ANNOTATION:
            case ScreenActionTypes.CREATE_ANNOTATION:
            case ScreenActionTypes.ENTER_STAGE_MODE:
            case ScreenActionTypes.CLOSE_STAGE_MODE_SIDEBAR:
                return this.deselectDot(state);

            case ScreenActionTypes.UNHIGHLIGHT_COMMENT:
                return this.unhighlightComment(state, action);

            case ScreenActionTypes.UPDATE_DOTPOPUP_REMARK_TYPE:
                return this.updateDotPopupRemarkType(state, action);

            case ScreenActionTypes.ADD_ANNOTATION:
                return this.handleAddAnnotation(state, action);

            case ComponentActionTypes.OPEN_COMPONENT:
                return this.handleOpenComponent(state);

            // Project action reducers
            case ProjectActionTypes.GET_PROJECT_DOTS:
                return this.getProjectDots(state);
            case ProjectActionTypes.SET_PROJECT_DOTS:
                return this.setProjectDots(state);

            default:
                return state;
        }
    }
}

export default DotsStore;
export { State as DotsStoreState };
